import { XCircle } from '@phosphor-icons/react';
import type { FlowItemDto } from '@reelplicate/api-client/model';
import { Button, Popover, PopoverContent, PopoverTrigger, Stack } from '@reelplicate/ui';
import { useState } from 'react';

import { canRewrite, RewriteLine } from './rewrite-line';
import { useRun } from './run-state';

/**
 * The "N held" chip on a node and its popover (Figma "Morning rush - v3"):
 * each held item's reason and a "Rewrite line" to fix it and check it again.
 */
export function HeldItems({ items }: { items: FlowItemDto[] }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${items.length} held`}
          className="nodrag text-caption text-status-warning-fg bg-status-warning-bg focus-visible:shadow-focus-ring inline-flex h-5 items-center rounded-xs px-1.5 font-semibold outline-none"
        >
          {items.length} held
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Held items"
        className="nodrag nowheel w-90 p-3"
      >
        <Stack gap={4}>
          {items.map((item) => (
            <HeldItem key={item.id} item={item} />
          ))}
        </Stack>
      </PopoverContent>
    </Popover>
  );
}

function HeldItem({ item }: { item: FlowItemDto }) {
  const { workspaceId, show } = useRun();
  const [editing, setEditing] = useState(false);

  return (
    <Stack gap={2}>
      <div className="flex items-start gap-2">
        <XCircle aria-hidden className="text-status-danger-fg mt-0.5 size-4 shrink-0" />
        <Stack gap={1} className="min-w-0">
          <span className="text-label-md text-text-primary">{item.summary}</span>
          <span className="text-body-sm text-text-secondary">{item.reason}</span>
        </Stack>
      </div>
      {canRewrite(item) &&
        (editing ? (
          <RewriteLine
            workspaceId={workspaceId}
            item={item}
            onCancel={() => setEditing(false)}
            onRevised={show}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => setEditing(true)}
          >
            Rewrite line
          </Button>
        ))}
    </Stack>
  );
}
